import { useState, useEffect, useRef, useCallback } from 'react'
import type { SoundEvent } from './useBoardSounds'
import { sanToSoundEvents } from './useBoardSounds'
import { useNavigate } from '@tanstack/react-router'
import { Chess, type Move, type Square } from 'chess.js'
import { toast } from '../../lib/toast'
import { api, type RunTrainingItemAttemptView, type RunTrainingItemOverview } from '../../lib/api'
import { useAuth } from '../../context/auth'
import { useChessTheme } from '../../hooks/useChessTheme'
import { resolvePieceSet } from '../../lib/themes'
import { useSolveSession } from '../../context/solveSession'
import type { SessionAttemptHistoryItem } from '../../context/solveSession'
import {
  computeDests,
  applyUci,
  resolveStep,
  resultsInCheckmate,
  playerColor,
  resolveOverviewBoardPosition,
  computeBoardSize,
  computeMaxBoardSize,
  clampBoardScale,
  readBoardScale,
  writeBoardScale,
  MOVE_FEEDBACK_SUCCESS_MS,
  WRONG_REVERT_MS,
  FAILED_TO_OVERVIEW_MS,
  TIMER_UPDATE_MS,
  INITIAL_OPPONENT_MOVE_DELAY_MS,
} from './boardPage.helpers'
import type { Mode, Orientation, PendingPromotion, MoveFeedbackResult, MoveFeedbackState, BoardState, FailedModeWrongMove } from './boardPage.helpers'

export type { Mode, Orientation, PendingPromotion, MoveFeedbackResult, BoardState, FailedModeWrongMove }

export type TimerState = {
  elapsedTenths: number
  targetMinSolveTenths: number | null
  targetMaxSolveTenths: number | null
}

export type OverviewState = {
  data: RunTrainingItemOverview | null
}

export type BoardPageActions = {
  handleUserMove: (orig: string, dest: string) => void
  onPromotionPieceSelected: (piece: 'q' | 'r' | 'b' | 'n') => void
  onPromotionCancel: () => void
  handleShowHint: () => void
  handleShowSolution: () => void
  handleRetake: () => Promise<void>
  handleNextPuzzle: () => Promise<void>
  dismissRunComplete: () => void
  resizeBoard: (size: number) => void
  commitBoardResize: () => void
}

export type BoardPageControllerResult = {
  mode: Mode
  solvingView: RunTrainingItemAttemptView | null
  board: BoardState
  timer: TimerState
  session: {
    attemptHistory: SessionAttemptHistoryItem[]
    movesPlayed: string[]
    allPliesPlayed: string[]
    failedRetryPlies: string[]
    failedModeWrongMoves: FailedModeWrongMove[]
    liveFocusStatus: 'in_progress' | 'solved' | 'failed'
  }
  overview: OverviewState
  isLoadingNextPuzzle: boolean
  runJustCompleted: boolean
  runCompleteOverlayData: NonNullable<RunTrainingItemOverview['runCompleteOverlay']> | null
  inputBlocked: boolean
  actions: BoardPageActions
}

export type BoardPageControllerParams = {
  runId: number
  runTrainingItemId: number
  attemptId: number | null
  runIdStr: string
  runTrainingItemIdStr: string
  routeKind: 'attempt' | 'overview'
  onPlaySound?: (event: SoundEvent) => void
}

export function useBoardPageController(params: BoardPageControllerParams): BoardPageControllerResult {
  const { runId, runTrainingItemId, attemptId, runIdStr, runTrainingItemIdStr, routeKind, onPlaySound } = params
  const playSoundRef = useRef<(event: SoundEvent) => void>(() => {})
  playSoundRef.current = onPlaySound ?? (() => {})
  const navigate = useNavigate()
  const { user } = useAuth()
  const { attemptHistory, registerAttemptStart, markAttemptResolved } = useSolveSession()
  useChessTheme(user?.boardTheme, user?.pieceTheme)

  const opponentMoveDelayRef = useRef(user?.opponentMoveDelayMs ?? 300)
  opponentMoveDelayRef.current = user?.opponentMoveDelayMs ?? 300
  const animationDurationRef = useRef(user?.animationDurationMs ?? 150)
  animationDurationRef.current = user?.animationDurationMs ?? 150

  const chessRef = useRef<Chess | null>(null)
  const modeRef = useRef<Mode>('loading')
  const moveIndexRef = useRef(1)
  const inputBlockedRef = useRef(false)
  const movesPlayedRef = useRef<string[]>([])
  const allPliesRef = useRef<string[]>([])
  const failedRetryPliesRef = useRef<string[]>([])
  const solutionMovesRef = useRef<(string | string[])[]>([])
  const currentAttemptIdRef = useRef<number | null>(null)
  const currentRunTrainingItemIdRef = useRef<number>(runTrainingItemId)
  const displayFenRef = useRef('')
  const elapsedRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pendingPromotionRef = useRef<PendingPromotion | null>(null)
  const committedLastMoveRef = useRef<[string, string] | undefined>(undefined)
  const moveFeedbackRef = useRef<MoveFeedbackState>({
    lastMoveResult: null,
    lastMoveSquare: null,
    isShowingMoveFeedback: false,
  })
  const failedUserMovesRef = useRef<string[]>([])
  const failedModeWrongMovesRef = useRef<FailedModeWrongMove[]>([])
  const concludeFnRef = useRef<() => Promise<void>>(async () => {})
  const concludingRef = useRef(false)
  const concludePromiseRef = useRef<Promise<void> | null>(null)
  const skipNextLoadRef = useRef(false)
  const cachedOverviewPuzzleRef = useRef<RunTrainingItemOverview | null>(null)
  const loadRequestIdRef = useRef(0)
  const isAttemptReadyRef = useRef(false)
  const latestResolvedAttemptIdRef = useRef<number | null>(null)
  const primeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  const revertPendingRef = useRef(false)

  const [mode, setMode] = useState<Mode>('loading')
  const [solvingView, setSolvingView] = useState<RunTrainingItemAttemptView | null>(null)
  const [overview, setOverview] = useState<RunTrainingItemOverview | null>(null)
  const [orientation, setOrientation] = useState<Orientation>('white')
  const [currentAttemptId, setCurrentAttemptId] = useState<number | null>(null)
  const [displayFen, setDisplayFen] = useState('')
  const [dests, setDests] = useState<Map<string, string[]>>(new Map())
  const [lastMove, setLastMove] = useState<[string, string] | undefined>(undefined)
  const [inputBlocked, setInputBlocked] = useState(false)
  const [pendingPromotion, setPendingPromotion] = useState<PendingPromotion | null>(null)
  const [hintSquare, setHintSquare] = useState<string | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [targetMinSolveTenths, setTargetMinSolveTenths] = useState<number | null>(null)
  const [targetMaxSolveTenths, setTargetMaxSolveTenths] = useState<number | null>(null)
  const [boardKey, setBoardKey] = useState(0)
  const [isLoadingNextPuzzle, setIsLoadingNextPuzzle] = useState(false)
  const [runJustCompleted, setRunJustCompleted] = useState(false)
  const [runCompleteOverlayData, setRunCompleteOverlayData] = useState<NonNullable<RunTrainingItemOverview['runCompleteOverlay']> | null>(null)
  const [movesPlayed, setMovesPlayed] = useState<string[]>([])
  const [allPliesPlayed, setAllPliesPlayed] = useState<string[]>([])
  const [failedRetryPlies, setFailedRetryPlies] = useState<string[]>([])
  const [failedModeWrongMoves, setFailedModeWrongMoves] = useState<FailedModeWrongMove[]>([])
  const [liveFocusStatus, setLiveFocusStatus] = useState<'in_progress' | 'solved' | 'failed'>('in_progress')
  const [isAttemptReady, setIsAttemptReady] = useState(false)
  const [lastMoveResult, setLastMoveResult] = useState<MoveFeedbackResult | null>(null)
  const [lastMoveSquare, setLastMoveSquare] = useState<string | null>(null)
  const [isShowingMoveFeedback, setIsShowingMoveFeedback] = useState(false)
  const [boardScale, setBoardScale] = useState(() => readBoardScale())
  const [boardSize, setBoardSize] = useState(() => computeBoardSize(boardScale))

  useEffect(() => {
    const compute = (): void => setBoardSize(computeBoardSize(boardScale))
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [boardScale])

  const boardScaleRef = useRef(boardScale)

  const resizeBoard = useCallback((size: number): void => {
    const next = clampBoardScale(size / computeMaxBoardSize())
    boardScaleRef.current = next
    setBoardScale(next)
  }, [])

  const commitBoardResize = useCallback((): void => {
    writeBoardScale(boardScaleRef.current)
  }, [])

  const setFen = useCallback((fen: string): void => {
    displayFenRef.current = fen
    setDisplayFen(fen)
  }, [])

  const setBlocked = useCallback((v: boolean): void => {
    inputBlockedRef.current = v
    setInputBlocked(v)
  }, [])

  const setPendingPromotionBoth = useCallback((v: PendingPromotion | null): void => {
    pendingPromotionRef.current = v
    setPendingPromotion(v)
  }, [])

  const setMoveFeedback = useCallback((
    result: MoveFeedbackResult | null,
    square: string | null,
    visible: boolean,
  ): void => {
    moveFeedbackRef.current = { lastMoveResult: result, lastMoveSquare: square, isShowingMoveFeedback: visible }
    setLastMoveResult(result)
    setLastMoveSquare(square)
    setIsShowingMoveFeedback(visible)
  }, [])

  const clearMoveFeedback = useCallback((): void => {
    setMoveFeedback(null, null, false)
  }, [setMoveFeedback])

  const hideMoveFeedbackBadge = useCallback((): void => {
    const current = moveFeedbackRef.current
    setMoveFeedback(current.lastMoveResult, current.lastMoveSquare, false)
  }, [setMoveFeedback])

  const clearOverviewState = useCallback((): void => {
    setOverview(null)
    setSolvingView(null)
    setRunCompleteOverlayData(null)
    cachedOverviewPuzzleRef.current = null
  }, [])

  const clearPendingTimeouts = useCallback((): void => {
    for (const timeoutId of pendingTimeoutsRef.current) {
      clearTimeout(timeoutId)
    }
    pendingTimeoutsRef.current.clear()
    revertPendingRef.current = false
  }, [])

  const scheduleTimeout = useCallback((callback: () => void, delayMs: number): ReturnType<typeof setTimeout> => {
    const timeoutId = setTimeout(() => {
      pendingTimeoutsRef.current.delete(timeoutId)
      callback()
    }, delayMs)
    pendingTimeoutsRef.current.add(timeoutId)
    return timeoutId
  }, [])

  const navigateToOverview = useCallback((selectedAttemptId: number | null, replace = false): void => {
    if (modeRef.current === 'focus' && currentAttemptIdRef.current !== null) {
      return
    }

    void navigate({
      to: '/app/runs/$runId/training-items/$runTrainingItemId/overview',
      params: { runId: runIdStr, runTrainingItemId: runTrainingItemIdStr },
      search: selectedAttemptId === null ? {} : { attempt: selectedAttemptId },
      replace,
    })
  }, [navigate, runIdStr, runTrainingItemIdStr])

  const applyFreshActiveState = useCallback((data: RunTrainingItemAttemptView): void => {
    clearPendingTimeouts()

    if (primeTimeoutRef.current !== null) {
      clearTimeout(primeTimeoutRef.current)
      primeTimeoutRef.current = null
    }

    const solutionMoves = data.trainingItem.solution
    const chess = new Chess(data.trainingItem.fen)

    chessRef.current = chess
    solutionMovesRef.current = solutionMoves
    moveIndexRef.current = 0
    inputBlockedRef.current = true
    revertPendingRef.current = false
    movesPlayedRef.current = []
    allPliesRef.current = []
    failedRetryPliesRef.current = []
    failedUserMovesRef.current = []
    failedModeWrongMovesRef.current = []
    setMovesPlayed([])
    setAllPliesPlayed([])
    setFailedRetryPlies([])
    setFailedModeWrongMoves([])
    setLiveFocusStatus('in_progress')
    currentAttemptIdRef.current = data.attempt.id
    currentRunTrainingItemIdRef.current = data.runTrainingItem.id
    concludingRef.current = false
    latestResolvedAttemptIdRef.current = null
    elapsedRef.current = 0
    committedLastMoveRef.current = undefined
    isAttemptReadyRef.current = false

    setSolvingView(data)
    setCurrentAttemptId(data.attempt.id)
    setOrientation(playerColor(data.trainingItem.fen))
    setElapsedSeconds(0)
    setPendingPromotionBoth(null)
    setBlocked(true)
    clearMoveFeedback()
    setHintSquare(null)
    setFen(data.trainingItem.fen)
    setDests(new Map())
    setLastMove(undefined)
    setIsAttemptReady(false)
    modeRef.current = 'focus'
    setMode('focus')

    primeTimeoutRef.current = setTimeout(() => {
      const ch = chessRef.current
      if (!ch) return
      const firstMove = resolveStep(solutionMovesRef.current[0] ?? '')
      let firstApplied: Move
      try {
        firstApplied = applyUci(ch, firstMove)
      } catch {
        return
      }
      allPliesRef.current = [firstMove]
      setAllPliesPlayed([firstMove])
      const lm: [string, string] = [firstMove.slice(0, 2), firstMove.slice(2, 4)]
      committedLastMoveRef.current = lm
      setFen(ch.fen())
      setLastMove(lm)
      setDests(computeDests(ch))
      moveIndexRef.current = 1
      for (const ev of sanToSoundEvents(firstApplied.san)) playSoundRef.current(ev)

      primeTimeoutRef.current = setTimeout(() => {
        primeTimeoutRef.current = null
        isAttemptReadyRef.current = true
        setIsAttemptReady(true)
        inputBlockedRef.current = false
        setInputBlocked(false)
      }, animationDurationRef.current)
    }, INITIAL_OPPONENT_MOVE_DELAY_MS)
  }, [clearMoveFeedback, clearPendingTimeouts, setFen, setBlocked, setPendingPromotionBoth])

  const applyOverviewDisplayState = useCallback((data: RunTrainingItemOverview): void => {
    const solutionMoves = data.trainingItem.solution
    const { fen: boardFen, lastMove: boardLastMove } = resolveOverviewBoardPosition(
      data.attempts,
      solutionMoves,
      data.trainingItem.fen,
    )

    chessRef.current = new Chess(data.trainingItem.fen)
    solutionMovesRef.current = solutionMoves
    moveIndexRef.current = 1
    inputBlockedRef.current = false
    movesPlayedRef.current = []
    currentAttemptIdRef.current = null
    elapsedRef.current = 0
    committedLastMoveRef.current = undefined

    setCurrentAttemptId(null)
    setOrientation(playerColor(data.trainingItem.fen))
    setElapsedSeconds(0)
    setPendingPromotionBoth(null)
    setBlocked(false)
    clearMoveFeedback()
    setHintSquare(null)
    setFen(boardFen)
    setDests(new Map())
    setLastMove(boardLastMove)
    setBoardKey((k) => k + 1)
    modeRef.current = 'overview'
    setMode('overview')
  }, [clearMoveFeedback, setFen, setBlocked, setPendingPromotionBoth])

  useEffect(() => {
    loadRequestIdRef.current += 1
    const requestId = loadRequestIdRef.current

    // Apply cached board state immediately (avoids flicker), but always
    // fetch the full overview from the API so PGN and other fields are fresh.
    let appliedCachedBoard = false
    if (skipNextLoadRef.current) {
      const cached = cachedOverviewPuzzleRef.current
      skipNextLoadRef.current = false
      cachedOverviewPuzzleRef.current = null
      if (cached !== null) {
        applyOverviewDisplayState(cached)
        appliedCachedBoard = true
      }
    }

    if (
      modeRef.current === 'focus' &&
      currentAttemptIdRef.current !== null &&
      currentAttemptIdRef.current === attemptId
    ) {
      return
    }

    void (async () => {
      try {
        if (routeKind === 'overview') {
          const { overview: overviewData } = await api.runs.getOverview(runId, runTrainingItemId, attemptId ?? undefined)

          if (requestId !== loadRequestIdRef.current) return

          setOverview(overviewData)
          if (!appliedCachedBoard) {
            applyOverviewDisplayState(overviewData)
          }
          return
        }

        if (attemptId === null) {
          toast.error('Invalid attempt', { description: 'Attempt not found for this puzzle.' })
          void navigate({ to: '/app/runs/$runId', params: { runId: runIdStr }, replace: true })
          return
        }

        const attemptResponse = await api.runs.getAttempt(runId, runTrainingItemId, attemptId)

        if (requestId !== loadRequestIdRef.current) return

        if (attemptResponse.kind === 'completed_attempt') {
          navigateToOverview(attemptId, true)
          return
        }

        const data = attemptResponse.attemptView
        setTargetMinSolveTenths(data.timer.targetMinSolveTenths)
        setTargetMaxSolveTenths(data.timer.targetMaxSolveTenths)
        applyFreshActiveState(data)
      } catch {
        if (requestId !== loadRequestIdRef.current) {
          return
        }
        toast.error('Failed to load puzzle', { description: 'Please try again.' })
        void navigate({ to: '/app/runs/$runId', params: { runId: runIdStr }, replace: true })
      }
    })()
  }, [
    runId,
    runTrainingItemId,
    attemptId,
    navigate,
    runIdStr,
    routeKind,
    applyFreshActiveState,
    applyOverviewDisplayState,
    navigateToOverview,
  ])

  // Redirect to the in-progress attempt route when arriving at the overview URL
  // while an attempt is active. Uses the ref (set synchronously in conclude())
  // rather than state to avoid a race where the overview URL is replaced right
  // after conclude() navigates here but before setCurrentAttemptId(null) flushes.
  useEffect(() => {
    if (routeKind !== 'overview') return
    if (currentAttemptIdRef.current === null) return
    void navigate({
      to: '/app/runs/$runId/training-items/$runTrainingItemId/attempts/$attemptId',
      params: { runId: runIdStr, runTrainingItemId: runTrainingItemIdStr, attemptId: String(currentAttemptIdRef.current) },
      replace: true,
    })
  }, [routeKind, navigate, runIdStr, runTrainingItemIdStr])

  useEffect(() => {
    if (mode !== 'focus' || !isAttemptReady) return
    timerRef.current = setInterval(() => {
      elapsedRef.current += TIMER_UPDATE_MS / 100
      setElapsedSeconds(elapsedRef.current)
    }, TIMER_UPDATE_MS)
    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [mode, isAttemptReady])

  useEffect(() => {
    return () => {
      clearPendingTimeouts()
      if (primeTimeoutRef.current !== null) {
        clearTimeout(primeTimeoutRef.current)
      }
    }
  }, [clearPendingTimeouts])

  useEffect(() => {
    if (currentAttemptId === null) return
    const handler = (e: BeforeUnloadEvent): void => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [currentAttemptId])

  useEffect(() => {
    if (mode !== 'focus') return
    if (solvingView === null || currentAttemptId === null) return
    registerAttemptStart({
      attemptId: currentAttemptId,
      runTrainingItemId: solvingView.runTrainingItem.id,
      puzzlePosition: solvingView.runTrainingItem.position + 1,
    })
  }, [mode, solvingView, currentAttemptId, registerAttemptStart])

  const enterFailed = useCallback((): void => {
    modeRef.current = 'failed'
    setMode('failed')
    setHintSquare(null)
    failedRetryPliesRef.current = []
    failedUserMovesRef.current = movesPlayedRef.current.slice(0, -1)
    setFailedRetryPlies([])
  }, [])

  const conclude = useCallback(async (): Promise<void> => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    const id = currentAttemptIdRef.current
    if (id === null) return

    concludingRef.current = true
    try {
      const result = await api.attempts.complete(runId, currentRunTrainingItemIdRef.current, id, movesPlayedRef.current, Math.round(elapsedRef.current * 100))

      if (currentAttemptIdRef.current !== id) {
        markAttemptResolved(id, result.outcome)
        latestResolvedAttemptIdRef.current = id
        return
      }

      markAttemptResolved(id, result.outcome)
      latestResolvedAttemptIdRef.current = id
      setCurrentAttemptId(null)
      currentAttemptIdRef.current = null

      setOverview(result.overview)

      if (result.runCompletedByThisAttempt) {
        setRunJustCompleted(true)
        if (result.overview.runCompleteOverlay !== null) {
          setRunCompleteOverlayData(result.overview.runCompleteOverlay)
        }
      }

      cachedOverviewPuzzleRef.current = result.overview

      if (result.outcome === 'solved') {
        skipNextLoadRef.current = true
        navigateToOverview(id, false)
      } else if (modeRef.current !== 'failed') {
        enterFailed()
      }
    } catch {
    } finally {
      concludingRef.current = false
      inputBlockedRef.current = false
      setInputBlocked(false)
    }
  }, [enterFailed, markAttemptResolved, navigateToOverview, runId])

  concludeFnRef.current = conclude

  const applyOpponentMove = useCallback((uci: string): void => {
    const chess = chessRef.current
    if (!chess) return
    let applied: Move
    try {
      applied = applyUci(chess, uci)
    } catch {
      return
    }
    if (modeRef.current === 'focus') {
      allPliesRef.current = [...allPliesRef.current, uci]
      setAllPliesPlayed(allPliesRef.current)
    } else if (modeRef.current === 'failed') {
      failedRetryPliesRef.current = [...failedRetryPliesRef.current, uci]
      setFailedRetryPlies(failedRetryPliesRef.current)
    }
    setFen(chess.fen())
    const lm: [string, string] = [uci.slice(0, 2), uci.slice(2, 4)]
    setLastMove(lm)
    committedLastMoveRef.current = lm
    setDests(computeDests(chess))
    moveIndexRef.current += 1
    for (const ev of sanToSoundEvents(applied.san)) playSoundRef.current(ev)
  }, [setFen])

  const resolveCorrectMove = useCallback((orig: string, dest: string, uci: string): void => {
    const chess = chessRef.current
    if (!chess) return

    let applied: Move
    try {
      applied = applyUci(chess, uci)
    } catch {
      return
    }
    if (modeRef.current === 'focus') {
      movesPlayedRef.current = [...movesPlayedRef.current, uci]
      setMovesPlayed(movesPlayedRef.current)
      allPliesRef.current = [...allPliesRef.current, uci]
      setAllPliesPlayed(allPliesRef.current)
    } else if (modeRef.current === 'failed') {
      failedUserMovesRef.current = [...failedUserMovesRef.current, uci]
      failedRetryPliesRef.current = [...failedRetryPliesRef.current, uci]
      setFailedRetryPlies(failedRetryPliesRef.current)
    }
    setFen(chess.fen())
    setLastMove([orig, dest])
    committedLastMoveRef.current = [orig, dest]
    setDests(computeDests(chess))
    setMoveFeedback('correct', dest, true)
    setHintSquare(null)
    moveIndexRef.current += 1

    const solutionMoves = solutionMovesRef.current
    const isLastMove = moveIndexRef.current >= solutionMoves.length
    for (const ev of sanToSoundEvents(applied.san)) playSoundRef.current(ev)

    if (isLastMove) {
      if (modeRef.current === 'focus') setLiveFocusStatus('solved')
      // For multi-accept (Decoy) puzzles solved in failed mode: fire appendVariation
      // immediately so the network request overlaps with the existing animation delay
      // rather than adding extra latency before the overview transition.
      const justPlayedWasMultiAccept = Array.isArray(solutionMoves[moveIndexRef.current - 1])
      let appendVariationPromise: Promise<void> | null = null
      if (modeRef.current === 'failed' && justPlayedWasMultiAccept) {
        const failedAttemptId = latestResolvedAttemptIdRef.current
        const rtiId = currentRunTrainingItemIdRef.current
        if (failedAttemptId !== null) {
          appendVariationPromise = api.attempts.appendVariation(runId, rtiId, failedAttemptId, failedUserMovesRef.current)
            .then(() => api.runs.getOverview(runId, rtiId, failedAttemptId))
            .then(({ overview: refreshed }) => {
              setOverview(refreshed)
              cachedOverviewPuzzleRef.current = refreshed
            })
            .catch(() => {})
        }
      }
      scheduleTimeout(() => {
        if (modeRef.current === 'focus') {
          void concludeFnRef.current()
        } else {
          scheduleTimeout(() => {
            void (async () => {
              if (concludePromiseRef.current !== null) {
                await concludePromiseRef.current
                concludePromiseRef.current = null
              }
              if (appendVariationPromise !== null) {
                await appendVariationPromise
              }
              skipNextLoadRef.current = true
              navigateToOverview(latestResolvedAttemptIdRef.current, false)
            })()
          }, FAILED_TO_OVERVIEW_MS)
        }
      }, MOVE_FEEDBACK_SUCCESS_MS)
      return
    }

    const opponentUci = resolveStep(solutionMoves[moveIndexRef.current] ?? '')
    scheduleTimeout(() => {
      hideMoveFeedbackBadge()
      applyOpponentMove(opponentUci)
    }, opponentMoveDelayRef.current)
  }, [setFen, applyOpponentMove, hideMoveFeedbackBadge, setMoveFeedback, navigateToOverview, scheduleTimeout])

  const resolveWrongMove = useCallback((
    orig: string,
    dest: string,
    promotionPiece?: 'q' | 'r' | 'b' | 'n',
  ): void => {
    const chess = chessRef.current
    if (!chess) return

    const prevFen = displayFenRef.current
    inputBlockedRef.current = true
    revertPendingRef.current = true
    setInputBlocked(true)
    setMoveFeedback('wrong', dest, true)

    const uci = orig + dest + (promotionPiece ?? '')
    if (modeRef.current === 'focus') {
      movesPlayedRef.current = [...movesPlayedRef.current, uci]
      setMovesPlayed(movesPlayedRef.current)
      setLiveFocusStatus('failed')
      // Fire conclude immediately — board revert is a concurrent visual delay
      concludePromiseRef.current = concludeFnRef.current()
    } else {
      // Failed mode: record the wrong move for the live PGN, fire appendVariation,
      // then refresh the Aggregate PGN for the eventual overview transition.
      // Skip if the same move at the same position was already recorded — including
      // W1 itself, which is tracked separately (not in failedModeWrongMoves).
      const currentRetryPlies = failedRetryPliesRef.current
      const posKey = currentRetryPlies.join(',')
      const w1 = movesPlayedRef.current[movesPlayedRef.current.length - 1]
      const w1PositionKey = allPliesRef.current.slice(1).join(',')
      const isDuplicate =
        (uci === w1 && posKey === w1PositionKey) ||
        failedModeWrongMovesRef.current.some(
          w => w.uci === uci && w.retryPliesAtWrongMove.join(',') === posKey,
        )
      if (!isDuplicate) {
        const wrongRecord: FailedModeWrongMove = {
          uci,
          retryPliesAtWrongMove: [...currentRetryPlies],
        }
        failedModeWrongMovesRef.current = [...failedModeWrongMovesRef.current, wrongRecord]
        setFailedModeWrongMoves(failedModeWrongMovesRef.current)

        const failedAttemptId = latestResolvedAttemptIdRef.current
        const rtiId = currentRunTrainingItemIdRef.current
        if (failedAttemptId !== null) {
          void api.attempts.appendVariation(runId, rtiId, failedAttemptId, [...failedUserMovesRef.current, uci])
            .then(() => api.runs.getOverview(runId, rtiId, failedAttemptId))
            .then(({ overview: updatedOverview }) => { setOverview(updatedOverview) })
            .catch(() => {})
        }
      }
    }

    const applied = chess.move({ from: orig, to: dest, promotion: promotionPiece ?? 'q' })
    setFen(chess.fen())
    setLastMove([orig, dest])
    for (const ev of sanToSoundEvents(applied.san)) playSoundRef.current(ev)

    scheduleTimeout(() => {
      chess.undo()
      displayFenRef.current = prevFen
      setDisplayFen(prevFen)
      setDests(computeDests(chess))
      setLastMove(committedLastMoveRef.current)
      hideMoveFeedbackBadge()
      setHintSquare(null)
      revertPendingRef.current = false
      if (modeRef.current === 'focus') {
        // conclude() may not have resolved yet — enter failed now
        enterFailed()
      }
      inputBlockedRef.current = false
      setInputBlocked(false)
    }, WRONG_REVERT_MS)
  }, [setFen, setMoveFeedback, hideMoveFeedbackBadge, scheduleTimeout, enterFailed])

  const handleUserMove = useCallback((orig: string, dest: string): void => {
    if (inputBlockedRef.current) return
    if (revertPendingRef.current) return
    if (concludingRef.current && modeRef.current === 'focus') return
    const chess = chessRef.current
    if (!chess) return
    if (modeRef.current !== 'focus' && modeRef.current !== 'failed') return

    const piece = chess.get(orig as Square)
    const isPromotion = piece?.type === 'p' && (dest[1] === '8' || dest[1] === '1')
    const solutionMoves = solutionMovesRef.current
    const moveIndex = moveIndexRef.current

    if (isPromotion) {
      const currentStep = solutionMoves[moveIndex]
      const expectedBase = resolveStep(currentStep ?? '').slice(0, 4)
      const promotionPieces = ['q', 'r', 'b', 'n'] as const
      const squareLeadsToCheckmate = promotionPieces.some((p) => resultsInCheckmate(chess, orig, dest, p))
      if (orig + dest !== expectedBase && !squareLeadsToCheckmate) {
        resolveWrongMove(orig, dest, 'q')
      } else {
        inputBlockedRef.current = true
        setInputBlocked(true)
        pendingPromotionRef.current = { orig, dest }
        setPendingPromotion({ orig, dest })
      }
      return
    }

    const uci = orig + dest
    const currentStep = solutionMoves[moveIndex]
    const isCorrect = Array.isArray(currentStep)
      ? currentStep.includes(uci)
      : uci === currentStep
    if (isCorrect || resultsInCheckmate(chess, orig, dest)) {
      resolveCorrectMove(orig, dest, uci)
    } else {
      resolveWrongMove(orig, dest)
    }
  }, [resolveCorrectMove, resolveWrongMove])

  const onPromotionPieceSelected = useCallback((piece: 'q' | 'r' | 'b' | 'n'): void => {
    const pending = pendingPromotionRef.current
    if (!pending) return
    pendingPromotionRef.current = null
    setPendingPromotion(null)
    inputBlockedRef.current = false
    setInputBlocked(false)

    const uci = pending.orig + pending.dest + piece
    const chess = chessRef.current
    const currentStep = solutionMovesRef.current[moveIndexRef.current]
    const isCorrect = Array.isArray(currentStep)
      ? currentStep.includes(uci)
      : uci === currentStep
    if (isCorrect || (chess !== null && resultsInCheckmate(chess, pending.orig, pending.dest, piece))) {
      resolveCorrectMove(pending.orig, pending.dest, uci)
    } else {
      resolveWrongMove(pending.orig, pending.dest, piece)
    }
  }, [resolveCorrectMove, resolveWrongMove])

  const onPromotionCancel = useCallback((): void => {
    pendingPromotionRef.current = null
    setPendingPromotion(null)
    inputBlockedRef.current = false
    setInputBlocked(false)
    setBoardKey((k) => k + 1)
  }, [])

  const handleShowHint = useCallback((): void => {
    const next = solutionMovesRef.current[moveIndexRef.current]
    if (!next) return
    setHintSquare(resolveStep(next).slice(0, 2))
  }, [])

  const handleShowSolution = useCallback((): void => {
    if (inputBlockedRef.current) return
    const chess = chessRef.current
    const solutionMoves = solutionMovesRef.current
    const moveIndex = moveIndexRef.current
    if (!chess || moveIndex >= solutionMoves.length) return

    const uci = resolveStep(solutionMoves[moveIndex] ?? '')
    inputBlockedRef.current = true
    setInputBlocked(true)
    try {
      applyUci(chess, uci)
    } catch {
      inputBlockedRef.current = false
      setInputBlocked(false)
      return
    }
    setFen(chess.fen())
    setLastMove([uci.slice(0, 2), uci.slice(2, 4)])
    setDests(computeDests(chess))
    setHintSquare(null)
    moveIndexRef.current += 1

    if (moveIndexRef.current >= solutionMoves.length) {
      scheduleTimeout(() => {
        inputBlockedRef.current = false
        setInputBlocked(false)
        skipNextLoadRef.current = true
        navigateToOverview(latestResolvedAttemptIdRef.current, false)
      }, FAILED_TO_OVERVIEW_MS)
      return
    }

    const opponentUci = resolveStep(solutionMoves[moveIndexRef.current] ?? '')
    scheduleTimeout(() => {
      applyOpponentMove(opponentUci)
      inputBlockedRef.current = false
      setInputBlocked(false)
    }, 150)
  }, [setFen, applyOpponentMove, navigateToOverview, scheduleTimeout])

  const handleNextPuzzle = useCallback(async (): Promise<void> => {
    setIsLoadingNextPuzzle(true)
    try {
      const data = await api.runs.continue(runId)

      if (data.runCompleted) {
        return
      }

      const av = data.attemptView
      if (av.trainingItem.solution.length < 2) {
        toast.error('Invalid puzzle', { description: 'Puzzle solution is too short.' })
        void navigate({ to: '/app/runs/$runId', params: { runId: runIdStr }, replace: true })
        return
      }

      clearOverviewState()
      setTargetMinSolveTenths(av.timer.targetMinSolveTenths)
      setTargetMaxSolveTenths(av.timer.targetMaxSolveTenths)
      applyFreshActiveState(av)
      setBoardKey((k) => k + 1)

      skipNextLoadRef.current = true
      void navigate({
        to: '/app/runs/$runId/training-items/$runTrainingItemId/attempts/$attemptId',
        params: {
          runId: runIdStr,
          runTrainingItemId: String(av.runTrainingItem.id),
          attemptId: String(av.attempt.id),
        },
      })
    } catch {
    } finally {
      setIsLoadingNextPuzzle(false)
    }
  }, [navigate, runId, runIdStr, applyFreshActiveState, clearOverviewState])

  const handleRetake = useCallback(async (): Promise<void> => {
    setIsLoadingNextPuzzle(true)
    try {
      const data = await api.runs.startTrainingItem(runId, runTrainingItemId)
      if (data.trainingItem.solution.length < 2) {
        toast.error('Invalid puzzle', { description: 'Puzzle solution is too short.' })
        return
      }

      clearOverviewState()
      setTargetMinSolveTenths(data.timer.targetMinSolveTenths)
      setTargetMaxSolveTenths(data.timer.targetMaxSolveTenths)
      applyFreshActiveState(data)
      setBoardKey((k) => k + 1)

      skipNextLoadRef.current = true
      void navigate({
        to: '/app/runs/$runId/training-items/$runTrainingItemId/attempts/$attemptId',
        params: {
          runId: runIdStr,
          runTrainingItemId: String(data.runTrainingItem.id),
          attemptId: String(data.attempt.id),
        },
      })
    } catch {
    } finally {
      setIsLoadingNextPuzzle(false)
    }
  }, [navigate, runId, runTrainingItemId, runIdStr, applyFreshActiveState, clearOverviewState])

  const dismissRunComplete = useCallback((): void => {
    setRunJustCompleted(false)
    setRunCompleteOverlayData(null)
  }, [])

  const turnToMove: Orientation = displayFen.split(' ')[1] === 'b' ? 'black' : 'white'
  const pieceSet = resolvePieceSet(user?.pieceTheme ?? '')
  const kingPieceUrl = turnToMove === 'white' ? pieceSet.pieces.wK : pieceSet.pieces.bK
  const darkKingPieceUrl = turnToMove === 'white' ? pieceSet.pieces.bK : pieceSet.pieces.wK

  return {
    mode,
    solvingView,
    board: {
      boardKey,
      boardSize,
      fen: displayFen,
      orientation,
      dests,
      lastMove,
      hintSquare,
      pendingPromotion,
      moveFeedback: { result: lastMoveResult, square: lastMoveSquare, visible: isShowingMoveFeedback },
      turnToMove,
      kingPieceUrl,
      darkKingPieceUrl,
    },
    timer: {
      elapsedTenths: elapsedSeconds,
      targetMinSolveTenths,
      targetMaxSolveTenths,
    },
    session: { attemptHistory, movesPlayed, allPliesPlayed, failedRetryPlies, failedModeWrongMoves, liveFocusStatus },
    overview: { data: overview },
    isLoadingNextPuzzle,
    runJustCompleted,
    runCompleteOverlayData,
    inputBlocked,
    actions: {
      handleUserMove,
      onPromotionPieceSelected,
      onPromotionCancel,
      handleShowHint,
      handleShowSolution,
      handleRetake,
      handleNextPuzzle,
      dismissRunComplete,
      resizeBoard,
      commitBoardResize,
    },
  }
}
